<script setup lang="ts">
import CreatePickupAuthorization from "@/shop/components/Authorization/CreatePickupAuthorization.vue"
import type { PickupAuthorization } from "@/model/backend/pickup-authorization"
import { useShopDialogs } from "@/shop/composables/useShopDialogs"
import { $date, useApp, useHttpClient } from "@/vf"
import { h, onMounted, ref } from "vue"

const http = useHttpClient()
const dialogs = useShopDialogs()
const app = useApp()

const pickupAuthorizations = ref<PickupAuthorization[]>([])
const authorizationForm = ref<InstanceType<typeof CreatePickupAuthorization> | null>(null)
const isSaving = ref(false)

async function loadAuthorizations() {
    try {
        const response = await http.get<PickupAuthorization[]>("pickup-authorization")
        pickupAuthorizations.value = response.data
    } catch (error) {
        console.error(error)
        await dialogs.alert(
            "Fehler",
            "Beim Laden der Vollmachten ist ein Fehler aufgetreten. Bitte lade die Seite neu.",
        )
    }
}

onMounted(() => {
    loadAuthorizations()
})

async function onCreated() {
    await loadAuthorizations()
}

async function remove(pickupAuthorization: PickupAuthorization) {
    if (!pickupAuthorization) {
        return
    }

    if (
        await dialogs.confirm(
            "Diese Vollmacht wirklich löschen?",
            () => [
                h("strong", "Wichtiger Hinweis:"),
                h(
                    "div",
                    "Das Löschen der Vollmacht wirkt sich nicht auf bereits bestehende Bestellungen aus. Wenn du die Vollmacht für eine bestehende Bestellung zurückziehen möchtest, kontaktiere uns bitte per Telefon.",
                ),
                h("div", "Tel.: 040 30 23 86 99 24"),
            ],
            {
                yesButton: controller =>
                    h(
                        "button",
                        {
                            type: "button",
                            class: "btn btn-danger",
                            onClick: () => controller.ok(true),
                        },
                        "Ja, für zukünftige Bestellungen löschen",
                    ),
            },
        )
    ) {
        try {
            await http.delete(`pickup-authorization/${pickupAuthorization.id}`)
            await loadAuthorizations()
        } catch (e) {
            await dialogs.alert("Fehler", "Die Vollmacht konnte nicht gelöscht werden.")
            console.error(e)
        }
    }
}
</script>

<template>
    <div class="container py-5">
        <h1>Vollmacht erteilen</h1>

        <CreatePickupAuthorization
            ref="authorizationForm"
            @created="onCreated"
            @cancel="$router.push({ name: 'my-account' })"
            @saving="isSaving = $event"
        />

        <div class="mt-4 d-flex gap-2 justify-content-between">
            <RouterLink type="button" class="btn btn-outline-secondary" :to="{ name: 'my-account' }">
                Zurück
            </RouterLink>
            <button type="button" :disabled="isSaving" class="btn btn-primary" @click="authorizationForm?.save()">
                {{ isSaving ? "Wird gespeichert..." : "Speichern" }}
            </button>
        </div>

        <template v-if="pickupAuthorizations.length">
            <hr class="my-5" />
            <h2 class="mb-4 mt-5">Bestehende Vollmachten</h2>
            <div class="row">
                <div
                    v-for="pickupAuthorization in pickupAuthorizations"
                    :key="pickupAuthorization.id"
                    class="col-lg-6 mb-3"
                >
                    <div class="card h-100 pickup-card">
                        <div class="card-body d-flex flex-column">
                            <div class="d-flex justify-content-between align-items-start mb-2 gap-2">
                                <div class="d-flex flex-column">
                                    <h5 class="card-title mb-1 text-primary">
                                        {{ pickupAuthorization.firstName }} {{ pickupAuthorization.lastName }}
                                    </h5>
                                </div>
                                <div class="action-buttons">
                                    <a
                                        v-if="pickupAuthorization.customerUploadId"
                                        type="button"
                                        class="btn btn-sm btn-outline-primary"
                                        title="Download"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        :href="
                                            app.apiLink(
                                                `shop/my-account/upload/${pickupAuthorization.customerUploadId}`,
                                            )
                                        "
                                    >
                                        <i class="fa fa-fw fa-download"></i>
                                    </a>
                                    <button
                                        type="button"
                                        class="btn btn-sm btn-outline-danger"
                                        title="Löschen"
                                        @click="remove(pickupAuthorization)"
                                    >
                                        <i class="fa fa-fw fa-trash"></i>
                                    </button>
                                </div>
                            </div>
                            <div class="card-text mt-auto">
                                <table>
                                    <tr>
                                        <td class="text-muted small pr-2">Geburtstag:</td>
                                        <td>{{ $date(pickupAuthorization.dateOfBirth) }}</td>
                                    </tr>
                                    <tr>
                                        <td class="text-muted small pr-2">Erstellt am:</td>
                                        <td>{{ $date(pickupAuthorization.createdAt) }}</td>
                                    </tr>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<style scoped>
.pickup-card {
    transition:
        transform 0.2s,
        box-shadow 0.2s;
    border: 1px solid rgba(0, 0, 0, 0.1);
}

.pickup-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.action-buttons {
    display: flex;
    gap: 0.5rem;
}

.action-buttons .btn {
    border-radius: 8px;
}

.card-title {
    font-weight: 600;
}
</style>
